import { useState } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import { agencyInfo } from '../../../mocks/dashboard';
import { useTenant } from '../../../context/TenantContext';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  children?: { label: string; path: string }[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: 'ri-dashboard-3-line', path: '/dashboard' },
  {
    label: 'CRM',
    icon: 'ri-contacts-book-2-line',
    path: '/crm',
    children: [
      { label: 'Leads', path: '/crm/leads' },
      { label: 'Customers', path: '/crm/customers' },
      { label: 'Tasks', path: '/crm/tasks' },
    ],
  },
  { label: 'Bookings', icon: 'ri-calendar-check-line', path: '/bookings' },
  { label: 'Products', icon: 'ri-price-tag-3-line', path: '/products' },
  { label: 'Itinerary Builder', icon: 'ri-map-2-line', path: '/itinerary' },
  {
    label: 'Finance',
    icon: 'ri-bill-line',
    path: '/finance',
    children: [
      { label: 'Invoices', path: '/invoices' },
      { label: 'Vouchers', path: '/vouchers' },
    ],
  },
  { label: 'Quotes', icon: 'ri-file-text-line', path: '/quotes' },
  { label: 'Suppliers', icon: 'ri-building-4-line', path: '/suppliers' },
  { label: 'Email System', icon: 'ri-mail-send-line', path: '/email' },
  { label: 'Lead Capture', icon: 'ri-user-received-2-line', path: '/lead-capture' },
  { label: 'WhatsApp', icon: 'ri-whatsapp-line', path: '/whatsapp' },
  { label: 'Settings', icon: 'ri-settings-4-line', path: '/settings' },
];

export default function Sidebar() {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<string[]>(['CRM', 'Finance']);
  const [collapsed, setCollapsed] = useState(false);
  const { currentUser, agencyProfile, signOut } = useTenant();
  const navigate = useNavigate();

  const brand = agencyProfile.brandColor || agencyInfo.brandColor;
  const agencyName = agencyProfile.name || agencyInfo.name;
  const agencyLogo = agencyProfile.logo || agencyInfo.logo;
  const agencyPlan = agencyProfile.plan || agencyInfo.plan;

  const ROLE_LABELS: Record<string, string> = {
    super_admin: 'Super Admin',
    owner: 'Agency Owner',
    agent: 'Agent',
    finance: 'Finance',
    viewer: 'Viewer',
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-white border-r border-gray-100 flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-100">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ backgroundColor: brand }}
        >
          {agencyLogo}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm truncate">{agencyName}</p>
            <span
              className="text-xs px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: `${brand}18`, color: brand }}
            >
              {agencyPlan}
            </span>
          </div>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0 ml-auto"
        >
          <i className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-base`} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map((item) => (
          <div key={item.label}>
            {item.children ? (
              <>
                <button
                  onClick={() => toggleGroup(item.label)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.path)
                      ? 'text-gray-800'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      <i
                        className={`ri-arrow-right-s-line text-base transition-transform duration-200 ${
                          openGroups.includes(item.label) ? 'rotate-90' : ''
                        }`}
                      />
                    </>
                  )}
                </button>
                {!collapsed && openGroups.includes(item.label) && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive: active }) =>
                          `block px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                            active
                              ? 'font-semibold'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`
                        }
                        style={({ isActive: active }) =>
                          active ? { color: brand, backgroundColor: `${brand}10` } : {}
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path}
                className={({ isActive: active }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    active
                      ? 'font-semibold'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`
                }
                style={({ isActive: active }) =>
                  active ? { color: brand, backgroundColor: `${brand}12` } : {}
                }
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            )}
          </div>
        ))}

        {/* Super Admin separator */}
        {!collapsed && (
          <div className="pt-2 mt-1 border-t border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-300 px-3 py-1.5">Platform</p>
          </div>
        )}
        <Link
          to="/super-admin"
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-gray-500 hover:text-gray-700 hover:bg-gray-50"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-star-line text-base" />
          </div>
          {!collapsed && <span>Super Admin Panel</span>}
        </Link>
      </nav>

      {/* User Profile */}
      <div className="p-3 border-t border-gray-100">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: brand }}
          >
            {currentUser.avatar}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{currentUser.name}</p>
              <p className="text-xs text-gray-400">{ROLE_LABELS[currentUser.role] || currentUser.role}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={async () => { await signOut(); navigate('/login'); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-400 cursor-pointer transition-colors"
            >
              <i className="ri-logout-box-r-line text-base" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
